// MessengerContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { therapistClient } from '../../../../PatientApps/apiClient';

// Create contexts
export const SocketContext = createContext(null);
export const MessengerContext = createContext({});

// Helper function for generating UUIDs
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Main context provider
export const MessengerProvider = ({ children, currentUser, initialChats = [] }) => {
  const [isMessengerOpen, setIsMessengerOpen] = useState(false);
  const [allChats, setAllChats] = useState(initialChats);
  const [activeChats, setActiveChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  useEffect(() => {
    // Set loading state
    setLoading(true);
    console.log('Messenger context initializing...');
    
    // Initialize socket connection
    console.log('Initializing socket connection');
    socketRef.current = io({
      withCredentials: true,
    });
    
    // Socket connection event listeners
    socketRef.current.on('connect', () => {
      console.log('Socket connected successfully with ID:', socketRef.current.id);
    });
    
    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });
    
    socketRef.current.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });
    
    // Message event listeners
    socketRef.current.on('new_message', (message) => {
      console.log('Received new message:', message);
      handleNewMessage(message);
    });
    
    socketRef.current.on('message_saved', (message) => {
      console.log('Message saved confirmation:', message);
    });
    
    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
    });
    
    console.log('Current user in socket setup:', currentUser);
    
    // Fetch existing chats from backend
    const fetchChats = async () => {
      try {
        console.log('Fetching chats for user:', currentUser.uuid);
        
        // Make API call to get chats
        const response = await fetch('/api/chats', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for session authentication
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch chats: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Chats fetched successfully:', data);
        
        // Transform backend data format to component format
        const formattedChats = data.map(chat => ({
          id: chat.chat_uuid,
          participants: chat.participants.map(p => ({
            id: p.uuid,
            name: p.name,
            isOnline: p.is_online || false,
            avatar: p.avatar || '',
            role: p.role
          })),
          messages: chat.messages.map(msg => ({
            id: msg.message_uuid,
            chatId: chat.chat_uuid,
            content: msg.content,
            senderId: msg.sender_uuid === currentUser.uuid ? 'current-user' : 'them',
            timestamp: new Date(msg.created_at).toLocaleTimeString(),
            read: msg.status === 'read'
          })),
          unreadCount: chat.unread_count || 0
        }));
        
        console.log('Formatted chats:', formattedChats);
        setAllChats(formattedChats);
      } catch (error) {
        console.error('Error fetching chats:', error);
        // If API call fails, fall back to initialChats (which could be mock data in dev)
        console.log('Falling back to initial chats:', initialChats);
        setAllChats(initialChats);
      } finally {
        setLoading(false);
      }
    };
    
    // Execute the fetch function
    fetchChats();
    
    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        console.log('Disconnecting socket');
        socketRef.current.disconnect();
      }
    };
  }, [currentUser.uuid, initialChats]);

  // Handle incoming messages
  const handleNewMessage = (message) => {
    console.log('Processing new message:', message);
    console.log('Current user in message handler:', currentUser);
    
    setAllChats(prev => {
      console.log('Current chats state:', prev);
      const existingChatIndex = prev.findIndex(chat => chat.id === message.chat_uuid);
      console.log('Existing chat index:', existingChatIndex);
      
      if (existingChatIndex >= 0) {
        // Add message to existing chat
        console.log('Adding message to existing chat:', prev[existingChatIndex]);
        
        const updatedChats = [...prev];
        const newMessageObj = {
          id: message.message_uuid,
          chatId: message.chat_uuid,
          content: message.content,
          senderId: message.patient_uuid === currentUser.uuid ? 'current-user' : 'them',
          timestamp: new Date().toLocaleTimeString(),
          read: false
        };
        
        console.log('Created message object:', newMessageObj);
        const updatedMessages = [...updatedChats[existingChatIndex].messages, newMessageObj];
        
        // Update unread count if chat is not active
        const isActive = activeChats.includes(message.chat_uuid);
        console.log('Is chat active:', isActive);
        const unreadCount = isActive ? 0 : (updatedChats[existingChatIndex].unreadCount || 0) + 1;
        console.log('New unread count:', unreadCount);
        
        updatedChats[existingChatIndex] = {
          ...updatedChats[existingChatIndex],
          messages: updatedMessages,
          unreadCount
        };
        
        console.log('Updated chat object:', updatedChats[existingChatIndex]);
        return updatedChats;
      } else {
        // Create a new chat for this message
        console.log('Creating new chat for message');
        
        // Determine participant details
        const participantId = message.patient_uuid !== currentUser.uuid 
          ? message.patient_uuid 
          : message.therapist_uuid;
        
        const participantRole = message.patient_uuid !== currentUser.uuid 
          ? 'Client' 
          : 'Therapist';
        
        console.log('Participant ID:', participantId);
        console.log('Participant role:', participantRole);
        
        const newChat = {
          id: message.chat_uuid,
          participants: [{ 
            id: participantId,
            name: "New Contact", // This should come from your backend
            isOnline: true,
            avatar: '',
            role: participantRole
          }],
          messages: [{
            id: message.message_uuid,
            chatId: message.chat_uuid,
            content: message.content,
            senderId: message.patient_uuid === currentUser.uuid ? 'current-user' : 'them',
            timestamp: new Date().toLocaleTimeString(),
            read: false
          }],
          unreadCount: 1
        };
        
        console.log('Created new chat object:', newChat);
        return [...prev, newChat];
      }
    });
  };

  // Toggle messenger visibility
  const toggleMessenger = () => {
    setIsMessengerOpen(prev => !prev);
  };
  
  // Open a chat
  // Updated openChat function for MessengerContext.js

// Open a chat
const openChat = async (chatId) => {
  // If chat is already active, do nothing
  if (activeChats.includes(chatId)) {
    console.log(`Chat ${chatId} is already active`);
    // Instead of showing "already active" message, just close the messenger panel
    setIsMessengerOpen(false);
    return;
  }
  
  console.log(`Opening chat room: ${chatId}`);
  
  // Join the chat room via socket
  socketRef.current.emit('join_chat', chatId);
  
  // First check if we already have this chat with messages in allChats
  const existingChat = allChats.find(c => 
    (c.id === chatId || c.chat_uuid === chatId) && 
    c.messages && 
    c.messages.length > 0
  );
  
  if (existingChat) {
    console.log(`Chat ${chatId} already has ${existingChat.messages.length} messages loaded`);
    // Add to active chats
    setActiveChats(prev => [...prev, chatId]);
    // Reset unread count
    setAllChats(prev => 
      prev.map(chat => 
        (chat.id === chatId || chat.chat_uuid === chatId)
          ? { ...chat, unreadCount: 0 }
          : chat
      )
    );
    // Mark messages as read
    markMessagesAsRead(chatId);
    // Close the messenger panel
    setIsMessengerOpen(false);
    return;
  }
  
  try {
    // Fetch chat history from API
    const response = await therapistClient.get(`/api/protected/therapist/chats/${chatId}`);
    
    if (response && response.data) {
      console.log('Fetched chat history:', response.data);
      
      // Add to active chats
      setActiveChats(prev => [...prev, chatId]);
      console.log(`Added ${chatId} to active chats`);
      
      // Update the chat with message history in allChats
      setAllChats(prev => {
        // Find if the chat already exists in the list
        const existingChatIndex = prev.findIndex(chat => chat.id === chatId || chat.chat_uuid === chatId);
        
        if (existingChatIndex >= 0) {
          // Update existing chat
          const updatedChats = [...prev];
          updatedChats[existingChatIndex] = {
            ...updatedChats[existingChatIndex],
            ...response.data,
            id: chatId,
            chat_uuid: chatId,
            unreadCount: 0, // Reset unread count
            messages: response.data.messages.map(msg => ({
              ...msg,
              read: true // Mark all messages as read
            }))
          };
          return updatedChats;
        } else {
          // Add as new chat
          return [...prev, {
            ...response.data,
            id: chatId,
            chat_uuid: chatId,
            unreadCount: 0
          }];
        }
      });
      
      // Mark messages as read on the server
      markMessagesAsRead(chatId);
    }
  } catch (error) {
    console.error('Error fetching chat history:', error);
    // Still add to active chats even if fetching history fails
    setActiveChats(prev => [...prev, chatId]);
  }
  
  // Close the messenger panel
  setIsMessengerOpen(false);
};
  
  // Mark messages as read in backend
  const markMessagesAsRead = async (chatId) => {
    try {
      console.log(`Marking messages as read for chat: ${chatId}`);
      
      // Make API call to mark messages as read using therapistClient
      const response = await therapistClient.post(`/api/protected/therapist/chats/${chatId}/read`);
      
      console.log('Messages marked as read successfully:', response.data);
      
    } catch (error) {
      console.error('Error marking messages as read:', error);
      // Don't revert UI state, just log the error
    }
  };
  
  // Close a chat
  const closeChat = (chatId) => {
    setActiveChats(prev => prev.filter(id => id !== chatId));
  };

  // Create a new chat
  // Create a new chat
const createChat = (contact) => {
    console.log('Creating chat with contact:', contact);
    
    // Check if chat already exists with this user
    const existingChat = allChats.find(chat => 
      chat.participants && 
      chat.participants.some(p => p.id === contact.id)
    );
    
    if (existingChat) {
      openChat(existingChat.id);
      return;
    }
    
    // Ensure contact has proper id field
    const enhancedContact = {
      ...contact,
      // Ensure id field is set from any available identifier
      id: contact.id || contact.patient_uuid || contact.uuid
    };
    
    // Create new chat
    const newChatId = generateUUID();
    const newChat = {
      id: newChatId,
      participants: [enhancedContact],
      // Store patient_uuid at chat level for easier access
      patient_uuid: enhancedContact.id || enhancedContact.patient_uuid || enhancedContact.uuid,
      messages: [],
      unreadCount: 0
    };
    
    console.log('Created new chat with enhanced contact:', newChat);
    
    setAllChats(prev => [...prev, newChat]);
    openChat(newChatId);
  };

  // Send a message
// Send a message
const sendMessage = (chatId, content) => {
    if (!content.trim()) return;
    
    // Prepare message payload for backend
    const chat = allChats.find(c => c.id === chatId);
    if (!chat || !chat.participants || !chat.participants[0]) return;
    
    const contact = chat.participants[0];
    const isPatient = currentUser.role === 'patient';

    
    // Get therapist_uuid and patient_uuid 
    // For therapist users: use currentUser.uuid as therapist_uuid and contact.id as patient_uuid
    // For patient users: use contact.id as therapist_uuid and currentUser.uuid as patient_uuid
      const therapist_uuid = localStorage.getItem('userUuid');

    let patient_uuid;
    
    // Determine patient_uuid with proper fallbacks
    if (isPatient) {
      // If user is a patient, use their UUID
      patient_uuid = currentUser.uuid;
    } else {
      // If user is a therapist, try to get patient UUID from contact
      if (contact.id) {
        patient_uuid = contact.id;
      } else if (contact.patient_uuid) {
        patient_uuid = contact.patient_uuid;
      } else {
        // Add more detailed logging for debugging
        console.error('Missing patient UUID in contact:', contact);
        console.log('Attempting to find patient UUID in chat object:', chat);
        
        // Try to extract from chat object if available
        if (chat.patient_uuid) {
          patient_uuid = chat.patient_uuid;
        } else {
          // Last resort: Try to find any property that might contain the UUID
          const possibleUuidProps = ['uuid', 'patientUuid', 'patient_id'];
          for (const prop of possibleUuidProps) {
            if (contact[prop]) {
              patient_uuid = contact[prop];
              console.log(`Found patient UUID in property ${prop}:`, patient_uuid);
              break;
            }
          }
        }
      }
    }
    
    // Construct payload
    const payload = {
      chat_uuid: chatId,
      therapist_uuid: therapist_uuid,
      patient_uuid: patient_uuid,
      content: content,
      message_type: 'text'
    };
    
    // Log the payload for debugging
    console.log('Sending message payload:', payload);
    console.log('Current user:', currentUser);
    console.log('Contact:', contact);
    
    // Check if we have a valid patient_uuid before sending
    if (!patient_uuid) {
      console.error('Cannot send message: Missing patient_uuid');
      // You could show an error to the user here
      return;
    }
    
    // Send message to server via socket
    socketRef.current.emit('send_message', payload);
    
    // Optimistically add message to UI
    const newMessage = {
      id: generateUUID(),
      chatId,
      content,
      senderId: 'current-user',
      timestamp: new Date().toLocaleTimeString(),
      read: true
    };
    
    setAllChats(prev => 
      prev.map(chat => 
        chat.id === chatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat
      )
    );
  };

  // Count total unread messages
  const unreadCount = allChats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);

  // Context value
  const contextValue = {
    isMessengerOpen,
    allChats,
    activeChats,
    loading,
    unreadCount,
    toggleMessenger,
    openChat,
    closeChat,
    createChat,
    sendMessage,
    currentUser,
    setAllChats,
    setLoading
  
  };

  return (
    <SocketContext.Provider value={socketRef.current}>
      <MessengerContext.Provider value={contextValue}>
        {children}
      </MessengerContext.Provider>
    </SocketContext.Provider>
  );
};

// Custom hook for using the messenger context
export const useMessenger = () => useContext(MessengerContext);
export const useSocket = () => useContext(SocketContext);